import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { formatarValor } from "../../utils/formatarValor";
import { useExamsPdf } from "./services/useExamsPdf";
import { useExames } from "../../context/ExamesContext";
import "./ResumoExames.css";
import {
  Button,
  Container,
  Snackbar,
  Alert,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DetalheExame from "./components/DetalheExame";

function ResumoExames() {
  const navigate = useNavigate();
  const { examesSelecionados: examesSelecionadosMap } = useExames();
  const [examesSelecionados, setExamesSelecionados] = useState([]);
  const [total, setTotal] = useState(0);
  const [examesExpandidos, setExamesExpandidos] = useState(new Set());
  const { gerarPdf, carregando } = useExamsPdf();
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  useEffect(() => {
    const examesArray = Array.from(examesSelecionadosMap.values());
    
    if (examesArray.length === 0) {
      navigate("/");
      return;
    }

    setExamesSelecionados(examesArray);
      const soma = examesArray.reduce((acc, exame) => {
      const preco = parseFloat(exame.preco) || 0;
      return acc + preco;
    }, 0);
    setTotal(soma);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examesSelecionadosMap.size, navigate]);

  const handleToggleExpandir = (exameId) => {
    const novosExpandidos = new Set(examesExpandidos);
    novosExpandidos.has(exameId) ? novosExpandidos.delete(exameId) : novosExpandidos.add(exameId);
    setExamesExpandidos(novosExpandidos);
  };

  const handleGerarPDF = async () => {
    try {
      const resultado = await gerarPdf(examesSelecionados);
      
      if (resultado.sucesso) {
        let message = `PDF gerado com ${resultado.examesProcessados.length} exame(s)`;
        
        if (resultado.examesSemOrientacoes.length > 0) {
          message += `. ${resultado.examesSemOrientacoes.length} exame(s) sem orientações específicas foram omitidos.`;
        }
        
        setSnackbar({ 
          open: true, 
          message, 
          severity: "success" 
        });
      } else {
        setSnackbar({ 
          open: true, 
          message: "Nenhum exame com orientações específicas foi encontrado.", 
          severity: "warning" 
        });
      }
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: "Erro ao gerar PDF. Tente novamente.", 
        severity: "error" 
      });
      console.error("Erro ao gerar PDF:", error);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (examesSelecionados.length === 0) {
    return (
      <Container maxWidth="lg">
        <div className="resumoExamesContainer">
          <div className="resumoExamesHeader">
            <Button onClick={() => navigate("/")}>
              Voltar
            </Button>
          </div>
          <div className="resumoExamesTitulo">Nenhum exame selecionado</div>
        </div>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <div className="resumoExamesContainer">
        {/* Botão voltar */}
        <div className="resumoExamesHeader">
          <Button 
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/")}
            className="voltarButton"
            style={{ marginLeft: '-8px' }}
          >
            Voltar
          </Button>
        </div>

        {/* Título */}
        <h2 className="resumoExamesTitulo">Exames selecionados</h2>

        {/* Lista de exames com valores */}
        <div className="resumoExamesLista">
          {examesSelecionados.map((exame, index) => (
            <div key={exame.id || index} className="resumoExameLinha">
              <span className="resumoExameNome">{exame.nome}</span>
              <span className="resumoExamePreco">
                {exame.preco !== undefined && exame.preco !== null
                  ? formatarValor(parseFloat(exame.preco) || 0)
                  : "Consultar preço"}
              </span>
            </div>
          ))}
          <div className="resumoExameTotalLinha">
            <span className="resumoExameTotalTexto">Total</span>
            <span className="resumoExameTotalValor">
              {formatarValor(total)}
            </span>
          </div>
        </div>

        {/* Seção Instruções de preparo */}
        <h3 className="resumoExamesInstrucoesTitulo">Instruções de preparo</h3>

        {/* Lista de exames clicáveis no mesmo estilo da página principal */}
        <div className="groupItem">
          {examesSelecionados.map((exame, index) => (
            <div key={exame.id || index}>
              <div 
                className={`exameItem ${index === 0 ? 'first' : ''} ${index === examesSelecionados.length - 1 && !examesExpandidos.has(exame.id) ? 'last' : ''} ${examesExpandidos.has(exame.id) ? 'expanded' : ''}`}
                onClick={() => handleToggleExpandir(exame.id)}
                style={{ cursor: 'pointer' }}
              >
                <div className="exameItemContentWrapper">
                  <div className="exameItemContent">
                    {exame.nome}
                  </div>
                  <div className="exameItemPreco">
                    {exame.preco !== undefined && exame.preco !== null
                      ? formatarValor(parseFloat(exame.preco) || 0)
                      : "Consultar preço"}
                  </div>
                </div>
                {examesExpandidos.has(exame.id) ? (
                  <KeyboardArrowUpIcon className="exameItemArrow" />
                ) : (
                  <KeyboardArrowDownIcon className="exameItemArrow" />
                )}
              </div>
              <div className={`exameItemDetalhes ${examesExpandidos.has(exame.id) ? 'expanded' : ''}`}>
                {examesExpandidos.has(exame.id) && <DetalheExame id={exame.id} preco={exame.preco} />}
              </div>
            </div>
          ))}
        </div>

        {/* Botão de download */}
        <div className="resumoExamesActions">
          <Button
            variant="contained"
            className="downloadButton"
            endIcon={<DownloadIcon />}
            onClick={handleGerarPDF}
            disabled={carregando}
          >
            {carregando ? "Gerando PDF..." : "Download de Instruções"}
          </Button>
        </div>
      </div>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default ResumoExames;

